import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { business, gallery, reviews } from "../config/site.config";

export const NAV = [
  { href: "#tour", label: "Color tour" },
  { href: "#services", label: "Services" },
  ...(gallery.show ? [{ href: "#projects", label: "Projects" }] : []),
  ...(reviews.show ? [{ href: "#reviews", label: "Reviews" }] : []),
  { href: "#contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled || open ? "bg-beige/85 shadow-[0_1px_0_rgba(28,25,23,0.1)] backdrop-blur-xl" : "bg-transparent"}`} data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
        <Logo />
        <nav aria-label="Main" className="ml-6 hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="relative flex min-h-11 items-center text-sm font-medium text-charcoal after:absolute after:bottom-2 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-wildberry after:transition-transform hover:after:scale-x-100" data-testid={`nav-${n.href.slice(1)}`}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href={business.phoneHref} className="hidden min-h-11 items-center gap-2 px-3 py-2 text-sm font-semibold text-charcoal hover:text-wildberry md:flex lg:hidden xl:flex" data-testid="header-call-button">
            <Phone className="h-4 w-4" /> {business.phoneDisplay}
          </a>
          <a href="#estimate" className="hidden h-11 items-center bg-charcoal px-5 text-xs font-semibold uppercase tracking-[0.16em] text-beige transition-colors hover:bg-wildberry sm:flex" data-testid="header-book-estimate-button">
            Book an estimate
          </a>
          <button type="button" className="flex h-11 w-11 items-center justify-center lg:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="mobile-menu" aria-label="Mobile" className="border-t border-charcoal/10 bg-beige px-5 pb-6 pt-2 lg:hidden" data-testid="mobile-menu">
          {[...NAV, { href: "#estimate", label: "Book an estimate" }].map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block border-b border-charcoal/10 py-4 font-display text-2xl font-medium text-charcoal" data-testid={`mobile-nav-${n.href.slice(1)}`}>
              {n.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
};

export const MobileCallBar = () => (
  <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 border-t border-charcoal/15 bg-beige/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" data-testid="mobile-call-bar">
    <a href={business.phoneHref} className="flex h-14 items-center justify-center gap-2 bg-wildberry text-sm font-semibold text-beige" data-testid="mobile-call-button">
      <Phone className="h-4 w-4" /> Call {business.phoneDisplay}
    </a>
    <a href="#estimate" className="flex h-14 items-center justify-center text-sm font-semibold text-charcoal" data-testid="mobile-estimate-button">
      Book an estimate
    </a>
  </div>
);
